import { useMemo } from 'react';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import { ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';
import { loadClients, isClientActiveInYear, getClientRevenueComponents } from '../../utils/clientDataLoader';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Tax season peaks: primary (Jan-Apr) and extension season (Sep-Oct)
const PRIMARY_SEASON = [0, 1, 2, 3]; // Jan-Apr
const EXTENSION_SEASON = [8, 9]; // Sep-Oct

// Compute committed tax prep from BTS client data — year + churn aware.
// Future-start clients excluded; mid-year churns pro-rated (e.g., a
// bookkeeping client churned April contributes monthly × 4 months,
// not full annual).
function getBTSCommitted() {
  return loadClients()
    .filter(c => c.division === 'bts' && c.status === 'Active' && isClientActiveInYear(c, 2026))
    .reduce((s, c) => s + getClientRevenueComponents(c, 2026).base, 0);
}

export default function TaxSeasonProgress({ global2026Monthly }) {
  const committedTaxPrep = getBTSCommitted();
  const progress = useMemo(() => {
    if (!global2026Monthly?.months) return null;

    // Collect monthly tax prep by month
    const monthlyTP = MONTH_KEYS.map((mk) =>
      global2026Monthly.months[mk]?.income?.tax_preparation || 0
    );

    // YTD collected (actual months only)
    const ytdCollected = monthlyTP.slice(0, ACTUALS_CUTOFF_2026).reduce((s, v) => s + v, 0);

    // Full year projected
    const fullYearProjected = monthlyTP.reduce((s, v) => s + v, 0);

    // Primary season (Jan-Apr) total
    const primaryTotal = PRIMARY_SEASON.reduce((s, i) => s + monthlyTP[i], 0);
    const primaryActual = PRIMARY_SEASON.filter(i => i < ACTUALS_CUTOFF_2026).reduce((s, i) => s + monthlyTP[i], 0);

    // Extension season (Sep-Oct) total
    const extensionTotal = EXTENSION_SEASON.reduce((s, i) => s + monthlyTP[i], 0);

    // Collection rate (actual collected vs committed)
    const collectionRate = committedTaxPrep > 0 ? ytdCollected / committedTaxPrep : 0;

    // Monthly breakdown for sparkline
    const monthlyBreakdown = MONTH_KEYS.map((mk, i) => ({
      month: MONTH_LABELS[i],
      amount: monthlyTP[i],
      isActual: i < ACTUALS_CUTOFF_2026,
    }));

    return {
      committed: committedTaxPrep,
      ytdCollected,
      remaining: committedTaxPrep - ytdCollected,
      fullYearProjected,
      collectionRate,
      primaryTotal,
      primaryActual,
      extensionTotal,
      monthlyBreakdown,
    };
  }, [global2026Monthly, committedTaxPrep]);

  if (!progress) return null;

  // Find the peak month
  const peakMonth = progress.monthlyBreakdown.reduce((max, m) => m.amount > max.amount ? m : max, progress.monthlyBreakdown[0]);

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Tax Season Progress</SectionLabel>

      {/* Committed vs Collected */}
      <div className="mt-2 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-0.5">
          Committed Engagements
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[28px] font-bold text-navy">{formatCurrency(progress.committed)}</span>
        </div>
      </div>

      {/* Collection progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-[10px] text-[#999] mb-1">
          <span>Collected: {formatCurrency(progress.ytdCollected)}</span>
          <span>Remaining: {formatCurrency(progress.remaining)}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full bg-deep-teal transition-all"
            style={{ width: `${Math.min(progress.collectionRate * 100, 100)}%` }}
          />
        </div>
        <div className="text-[10px] text-[#999] mt-0.5">
          {(progress.collectionRate * 100).toFixed(1)}% collected through {MONTH_LABELS[ACTUALS_CUTOFF_2026 - 1]}
        </div>
      </div>

      {/* Monthly mini-bars */}
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] mb-2">Monthly Breakdown</div>
        <div className="flex items-end gap-1" style={{ height: 48 }}>
          {progress.monthlyBreakdown.map((m) => {
            const maxVal = peakMonth.amount || 1;
            const barHeight = Math.max(Math.round((m.amount / maxVal) * 44), 2);
            return (
              <div
                key={m.month}
                className="flex-1"
                title={`${m.month}: ${formatCurrency(m.amount)}`}
              >
                <div
                  className={`w-full rounded-t-[2px] ${m.isActual ? 'bg-deep-teal' : 'bg-deep-teal/30'}`}
                  style={{ height: barHeight, marginTop: 48 - barHeight }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex gap-1 mt-1">
          {progress.monthlyBreakdown.map((m) => (
            <div key={m.month} className="flex-1 text-center text-[8px] text-[#bbb]">
              {m.month.charAt(0)}
            </div>
          ))}
        </div>
      </div>

      {/* Season breakdown */}
      <div className="space-y-1.5 pt-3 border-t border-gray-100 text-[12px]">
        <div className="flex justify-between">
          <span className="text-[#888]">Filing Season (Jan-Apr)</span>
          <span className="font-bold text-navy">{formatCurrency(progress.primaryTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#888]">Extension Season (Sep-Oct)</span>
          <span className="font-bold text-navy">{formatCurrency(progress.extensionTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#888]">Other Months</span>
          <span className="font-bold text-navy">
            {formatCurrency(progress.fullYearProjected - progress.primaryTotal - progress.extensionTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}
